/**
 * Razorpay Payment Button Component
 * Integrates with biogas transaction history for payments
 */

import React, { useState } from 'react';
import { CreditCard, Loader2, CheckCircle, XCircle, Edit2, X } from 'lucide-react';
import {
  loadRazorpayScript,
  createOrder,
  verifyPayment,
  displayRazorpayCheckout,
  type RazorpayOrderRequest,
  type RazorpayPaymentVerificationRequest,
} from '../../services/razorpayService';

interface RazorpayPaymentButtonProps {
  transactionId: number;
  amount: number;
  maxAmount?: number; // Maximum allowed amount (from Amount column) - edited amount cannot exceed this
  gaushalaName: string;
  gaushalaId?: number;
  description?: string;
  onSuccess: (paymentId: string, orderId: string, paidAmount?: number) => void;
  onFailure: (error: string) => void;
  disabled?: boolean;
  className?: string;
  allowAmountEdit?: boolean; // New prop to enable amount editing
  showVerifyAndPay?: boolean; // New prop to show verify and pay option
}

export const RazorpayPaymentButton: React.FC<RazorpayPaymentButtonProps> = ({
  transactionId,
  amount,
  maxAmount,
  gaushalaName,
  gaushalaId,
  description,
  onSuccess,
  onFailure,
  disabled = false,
  className = '',
  allowAmountEdit = false,
  showVerifyAndPay = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState(amount);
  const [currentAmount, setCurrentAmount] = useState(amount);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Calculate the effective max amount (use maxAmount prop if provided, otherwise use original amount)
  const effectiveMaxAmount = maxAmount !== undefined ? maxAmount : amount;

  const handleAmountEdit = () => {
    setIsEditingAmount(true);
    setEditedAmount(currentAmount);
  };

  const handleAmountSave = () => {
    if (editedAmount <= 0) {
      setErrorMessage('Amount must be greater than zero');
      return;
    }

    // Validate that edited amount does not exceed the max amount (Amount column value)
    if (editedAmount > effectiveMaxAmount) {
      setErrorMessage(`Amount cannot exceed ₹${effectiveMaxAmount.toFixed(2)} (original transaction amount)`);
      return;
    }

    setCurrentAmount(editedAmount);
    setIsEditingAmount(false);
    setErrorMessage('');
  };

  const handleAmountCancel = () => {
    setEditedAmount(currentAmount);
    setIsEditingAmount(false);
  };

  // Handle Verify and Pay - shows verification modal first
  const handleVerifyAndPay = () => {
    setShowVerificationModal(true);
  };

  // Proceed with payment after verification
  const handleProceedWithPayment = () => {
    setShowVerificationModal(false);
    handlePayment();
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);
      setPaymentStatus('processing');
      setErrorMessage('');

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Create order with current (possibly edited) amount
      const orderRequest: RazorpayOrderRequest = {
        transactionId,
        amount: currentAmount,
        gaushalaName,
        gaushalaId,
        description: description || `Payment for transaction #${transactionId}`,
      };

      const orderResponse = await createOrder(orderRequest);

      if (!orderResponse.success || !orderResponse.data) {
        throw new Error(orderResponse.error || 'Failed to create payment order');
      }

      const orderData = orderResponse.data;

      // Display Razorpay checkout
      displayRazorpayCheckout(
        {
          orderId: orderData.orderId,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'SAUBHAGYA Biogas',
          description: orderRequest.description,
          keyId: orderData.razorpayKeyId,
          prefill: {
            name: gaushalaName,
          },
          notes: {
            transactionId: transactionId.toString(),
            gaushalaName,
          },
        },
        // On success
        async (response) => {
          try {
            // Verify payment
            const verificationRequest: RazorpayPaymentVerificationRequest = {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              transactionId,
            };

            const verificationResponse = await verifyPayment(verificationRequest);

            if (verificationResponse.success && verificationResponse.data) {
              setPaymentStatus('success');
              // Pass the current amount (which may have been edited) to the success callback
              onSuccess(
                verificationResponse.data.paymentId,
                verificationResponse.data.orderId,
                currentAmount
              );
            } else {
              throw new Error(verificationResponse.error || 'Payment verification failed');
            }
          } catch (verifyError) {
            const message = verifyError instanceof Error ? verifyError.message : 'Payment verification failed';
            setPaymentStatus('failed');
            setErrorMessage(message);
            onFailure(message);
          } finally {
            setIsLoading(false);
          }
        },
        // On failure
        (error) => {
          const message = error.description || error.message || 'Payment failed';
          setPaymentStatus('failed');
          setErrorMessage(message);
          setIsLoading(false);
          onFailure(message);
        }
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to initiate payment';
      setPaymentStatus('failed');
      setErrorMessage(message);
      setIsLoading(false);
      onFailure(message);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Amount Editor */}
      {allowAmountEdit && isEditingAmount ? (
        <div className="flex flex-col gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">₹</span>
            <input
              type="number"
              min="0.01"
              max={effectiveMaxAmount}
              step="0.01"
              value={editedAmount}
              onChange={(e) => {
                const newValue = parseFloat(e.target.value) || 0;
                setEditedAmount(newValue);
                // Clear error if value is now valid
                if (newValue > 0 && newValue <= effectiveMaxAmount) {
                  setErrorMessage('');
                }
              }}
              className={`flex-1 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                editedAmount > effectiveMaxAmount ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              autoFocus
            />
            <button
              onClick={handleAmountSave}
              disabled={editedAmount <= 0 || editedAmount > effectiveMaxAmount}
              className={`px-3 py-1 rounded text-sm font-medium ${
                editedAmount <= 0 || editedAmount > effectiveMaxAmount
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              title="Save amount"
            >
              Save
            </button>
            <button
              onClick={handleAmountCancel}
              className="px-2 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Max amount hint */}
          <div className="text-xs text-gray-600">
            Max allowed: ₹{effectiveMaxAmount.toFixed(2)}
            {editedAmount > effectiveMaxAmount && (
              <span className="text-red-600 ml-2">
                (Exceeds limit by ₹{(editedAmount - effectiveMaxAmount).toFixed(2)})
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {/* Main Pay Button or Verify and Pay Button */}
          {showVerifyAndPay ? (
            <button
              onClick={handleVerifyAndPay}
              disabled={disabled || isLoading}
              className={`
                flex-1 inline-flex items-center justify-center gap-2 px-4 py-2
                rounded-lg font-medium transition-all duration-200
                ${disabled || isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                }
                ${className}
              `}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : paymentStatus === 'success' ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Payment Successful</span>
                </>
              ) : paymentStatus === 'failed' ? (
                <>
                  <XCircle className="w-4 h-4" />
                  <span>Payment Failed</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Verify & Pay ₹{currentAmount.toFixed(2)}</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handlePayment}
              disabled={disabled || isLoading}
              className={`
                flex-1 inline-flex items-center justify-center gap-2 px-4 py-2
                rounded-lg font-medium transition-all duration-200
                ${disabled || isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 active:scale-95'
                }
                ${className}
              `}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : paymentStatus === 'success' ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Payment Successful</span>
                </>
              ) : paymentStatus === 'failed' ? (
                <>
                  <XCircle className="w-4 h-4" />
                  <span>Payment Failed</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  <span>Pay ₹{currentAmount.toFixed(2)}</span>
                </>
              )}
            </button>
          )}

          {/* Edit Amount Button */}
          {allowAmountEdit && paymentStatus === 'idle' && !isLoading && (
            <button
              onClick={handleAmountEdit}
              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all"
              title="Edit amount"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {errorMessage && (
        <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded border border-red-200">
          {errorMessage}
        </div>
      )}

      {paymentStatus === 'success' && (
        <div className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded border border-green-200">
          Payment completed successfully!
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Verify Payment Details</h3>
              <button
                onClick={() => setShowVerificationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Transaction ID:</span>
                  <span className="font-mono font-medium text-gray-900">#{transactionId}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Gaushala:</span>
                  <span className="font-medium text-gray-900">{gaushalaName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Original Amount:</span>
                  <span className="font-medium text-gray-900">₹{effectiveMaxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center border-t pt-3">
                  <span className="text-sm font-medium text-gray-700">Payment Amount:</span>
                  <span className="text-lg font-bold text-green-600">₹{currentAmount.toFixed(2)}</span>
                </div>
                {currentAmount !== effectiveMaxAmount && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                    Note: Paying ₹{(effectiveMaxAmount - currentAmount).toFixed(2)} less than original amount
                  </div>
                )}
              </div>

              {description && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Description:</span> {description}
                </div>
              )}

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ Please verify the payment details before proceeding. This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowVerificationModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleProceedWithPayment}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Confirm & Pay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RazorpayPaymentButton;
